import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { getFunctions, httpsCallable } from 'firebase/functions';

const CommunityContext = createContext(null);

export function CommunityProvider({ children }) {
  const { user } = useAuth();
  const functions = getFunctions();

  // Core state
  const [community, setCommunity] = useState(null);
  const [residents, setResidents] = useState([]);
  const [staff, setStaff] = useState([]);
  const [callerRole, setCallerRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Search and filter state
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all', 'not_started', 'in_progress', 'complete'
  const [careLevelFilter, setCareLevelFilter] = useState('all');
  const [sortField, setSortField] = useState('lastName');
  const [sortDirection, setSortDirection] = useState('asc');

  const { communityId: communityIdFromParams } = useParams();
  const communityId = communityIdFromParams || user?.communityId;
  const isAdmin = callerRole === 'admin';

  // ============================================
  // Load community data
  // ============================================

  const loadDashboard = useCallback(async () => {
    if (!communityId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const loadCommunityDashboard = httpsCallable(functions, 'loadCommunityDashboard');
      const result = await loadCommunityDashboard({ communityId });

      if (result.data.success) {
        setCommunity(result.data.community);
        setResidents(result.data.residents);
        setStaff(result.data.staff);
        setCallerRole(result.data.callerRole);
      }
    } catch (err) {
      console.error('Error loading community dashboard:', err);
      setError('Failed to load community data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [communityId, functions]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // ============================================
  // Resident management
  // ============================================

  const addResident = useCallback(async (residentData) => {
    try {
      const addCommunityResident = httpsCallable(functions, 'addCommunityResident');
      const result = await addCommunityResident({
        communityId,
        ...residentData
      });

      if (result.data.success) {
        // Add to local state immediately
        setResidents(prev => [...prev, {
          ...result.data.resident,
          story: {
            id: result.data.storyId,
            completionPercentage: 0,
            status: 'not_started',
            hasConsent: false,
            hasSummaries: false,
            answeredCount: 0
          }
        }]);
      }

      return result.data;
    } catch (err) {
      console.error('Error adding resident:', err);
      throw err;
    }
  }, [communityId, functions]);

  const importResidents = useCallback(async (residentsArray) => {
    try {
      const importFn = httpsCallable(functions, 'importResidents');
      const result = await importFn({
        communityId,
        residents: residentsArray
      });

      if (result.data.success) {
        // Reload dashboard to get fresh data
        await loadDashboard();
      }

      return result.data.results;
    } catch (err) {
      console.error('Error importing residents:', err);
      throw err;
    }
  }, [communityId, functions, loadDashboard]);

  const updateResident = useCallback(async (residentId, updates) => {
    try {
      const updateFn = httpsCallable(functions, 'updateCommunityResident');
      await updateFn({ communityId, residentId, updates });

      // Update local state
      setResidents(prev => prev.map(r =>
        r.id === residentId ? { ...r, ...updates } : r
      ));

      return true;
    } catch (err) {
      console.error('Error updating resident:', err);
      throw err;
    }
  }, [communityId, functions]);

  // ============================================
  // Staff management
  // ============================================

  const inviteStaff = useCallback(async ({ email, name, role }) => {
    try {
      const inviteFn = httpsCallable(functions, 'inviteStaff');
      const result = await inviteFn({ communityId, email, name, role });
      return result.data;
    } catch (err) {
      console.error('Error inviting staff:', err);
      throw err;
    }
  }, [communityId, functions]);

  const removeStaff = useCallback(async (staffUid) => {
    try {
      const removeFn = httpsCallable(functions, 'removeStaff');
      await removeFn({ communityId, staffUid });

      // Update local state
      setStaff(prev => prev.filter(s => s.id !== staffUid));

      return true;
    } catch (err) {
      console.error('Error removing staff:', err);
      throw err;
    }
  }, [communityId, functions]);

  // ============================================
  // Filtered and sorted residents
  // ============================================

  const filteredResidents = residents
    .filter(r => {
      // Search filter
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        const matchesName = r.fullName?.toLowerCase().includes(q) ||
          r.preferredName?.toLowerCase().includes(q);
        const matchesRoom = r.roomNumber?.toLowerCase().includes(q);
        if (!matchesName && !matchesRoom) return false;
      }

      // Status filter
      if (statusFilter !== 'all') {
        const storyStatus = r.story?.status || 'not_started';
        const completion = r.story?.completionPercentage || 0;

        if (statusFilter === 'not_started' && storyStatus !== 'not_started') return false;
        if (statusFilter === 'in_progress' && (storyStatus === 'not_started' || completion >= 100)) return false;
        if (statusFilter === 'complete' && completion < 100) return false;
      }

      // Care level filter
      if (careLevelFilter !== 'all') {
        if (careLevelFilter === 'unassigned') {
          if (r.careLevel) return false;
        } else {
          if (r.careLevel !== careLevelFilter) return false;
        }
      }

      return true;
    })
    .sort((a, b) => {
      let aVal, bVal;

      switch (sortField) {
        case 'lastName':
          aVal = a.lastName?.toLowerCase() || '';
          bVal = b.lastName?.toLowerCase() || '';
          break;
        case 'roomNumber':
          aVal = a.roomNumber || '';
          bVal = b.roomNumber || '';
          break;
        case 'completion':
          aVal = a.story?.completionPercentage || 0;
          bVal = b.story?.completionPercentage || 0;
          break;
        default:
          aVal = a.lastName?.toLowerCase() || '';
          bVal = b.lastName?.toLowerCase() || '';
      }

      if (sortDirection === 'asc') {
        return aVal < bVal ? -1 : aVal > bVal ? 1 : 0;
      }
      return aVal > bVal ? -1 : aVal < bVal ? 1 : 0;
    });

  // ============================================
  // Stats
  // ============================================

  const stats = {
    totalResidents: residents.length,
    notStarted: residents.filter(r => (r.story?.status || 'not_started') === 'not_started').length,
    inProgress: residents.filter(r => {
      const s = r.story;
      return s && s.status !== 'not_started' && (s.completionPercentage || 0) < 100;
    }).length,
    complete: residents.filter(r => (r.story?.completionPercentage || 0) >= 100).length,
    hasSummaries: residents.filter(r => r.story?.hasSummaries).length,
    averageCompletion: residents.length > 0
      ? Math.round(residents.reduce((sum, r) => sum + (r.story?.completionPercentage || 0), 0) / residents.length)
      : 0
  };

  // ============================================
  // Context value
  // ============================================

  const value = {
    // Data
    community,
    residents: filteredResidents,
    allResidents: residents,
    staff,
    callerRole,
    isAdmin,
    communityId,
    stats,

    // State
    loading,
    error,

    // Search and filter
    searchQuery,
    setSearchQuery,
    statusFilter,
    setStatusFilter,
    careLevelFilter,
    setCareLevelFilter,
    sortField,
    setSortField,
    sortDirection,
    setSortDirection,

    // Actions
    loadDashboard,
    addResident,
    importResidents,
    updateResident,
    inviteStaff,
    removeStaff
  };

  return (
    <CommunityContext.Provider value={value}>
      {children}
    </CommunityContext.Provider>
  );
}

export function useCommunity() {
  const context = useContext(CommunityContext);
  if (!context) {
    throw new Error('useCommunity must be used within a CommunityProvider');
  }
  return context;
}

export default CommunityContext;
